import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';

import { useExecution } from '../../hooks/queries/useExecution';
import { queryKeys } from '../../hooks/queries/queryKeys';
import apiService from '../../services/api';
import { RunStatus, RunResult } from '../../types';
import EnhancedStatusIndicator from '../workflow/EnhancedStatusIndicator';
import EnhancedProgressBar from '../workflow/EnhancedProgressBar';

interface CompactRunProgressCardProps {
  /**
   * The orchestration being run. Drives the `useExecution(orchestrationId)`
   * subscription. For an assistant-triggered run the editor's SSE handler is
   * NOT mounted, so this card drives its OWN live refresh via a `refetchInterval`
   * that polls while the run is non-terminal and STOPS once it reaches a terminal
   * state (see `useExecution` call below).
   */
  orchestrationId: number;
  /**
   * The pre-created run instance the confirm authorized. Used to confirm the
   * card is tracking the run the user just started (the `useExecution` snapshot
   * surfaces the latest run for the workflow); kept for the deep link + replay
   * correlation.
   */
  runInstanceId?: number;
  /**
   * Total step count from the run preview, used as the progress-bar
   * denominator. The execution snapshot does not carry the orchestration's full
   * step list, so the preview's count is the authoritative total here.
   */
  stepCount?: number;
  /** Owning project, for the Open-workflow deep link. */
  projectId?: number;
  /** Workflow name for the heading / link label. */
  workflowName?: string;
}

/**
 * CompactRunProgressCard (AC-1785, epic AC-1778). The in-chat success signal for
 * a workflow the assistant just ran on the user's confirm. It subscribes to
 * `useExecution(orchestrationId)` — the editor's existing run-status cache key —
 * and renders a compact live view (status pill + progress bar) plus an
 * Open-workflow deep link to the full editor run panel.
 *
 * It owns NO run lifecycle (the frontend already fired `apiService.runWorkflow`
 * after confirm), but it DOES own its own freshness: an assistant-triggered run
 * is not driven by the editor, so the editor's SSE handler — which is what
 * invalidates `queryKeys.workflowExecution` on terminal status — is not mounted.
 * Without a refresh of its own the card would freeze on its first snapshot
 * (e.g. "Running 50%") forever even after the backend run completes. So the card
 * passes a FUNCTION-form `refetchInterval` to `useExecution`: poll every ~2.5s
 * while the run is non-terminal, and STOP once it reaches completed / failed /
 * stopped. Deliberately lighter than the editor's full run panel — the chat shows
 * pending / running / completed / failed and points at the editor for per-step
 * detail.
 *
 * Laid out for a ~320px assistant panel: a labelled role="group" region, the
 * status pill, a thin progress bar (when running/known), and a full-width Open
 * link with a >=44px tap target.
 */

/** Run statuses that are terminal — no further polling needed once reached. */
const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set([
  'completed',
  'failed',
  'stopped',
]);

/**
 * Coerce a step's `final_output` into a readable string for the inline summary.
 *
 * A RunResult's `final_output` is typed `string` but in practice the executor
 * persists several shapes depending on the agent/tool that produced the terminal
 * step: a plain string, a markdown string, a JSON-looking string, or — once
 * deserialized upstream — a structured object/array. This extractor handles each
 * defensively and always returns a printable string (never throws):
 *   - string that parses as JSON  -> recurse into the parsed value
 *   - plain / markdown string      -> returned as-is
 *   - object with `.content`/`.message` -> that field
 *   - array of {type:'text', text} -> the first text chunk
 *   - anything else                -> JSON.stringify
 */
function extractSummaryText(value: unknown): string {
  if (value == null) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Only attempt a parse when it actually looks like JSON, so ordinary
    // prose/markdown is never mangled.
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return extractSummaryText(JSON.parse(trimmed));
      } catch {
        return value;
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    // Array-of-content-blocks: find the first {type:'text', text}.
    const textBlock = value.find(
      (b): b is { type: string; text: string } =>
        b != null &&
        typeof b === 'object' &&
        (b as any).type === 'text' &&
        typeof (b as any).text === 'string',
    );
    if (textBlock) return textBlock.text;
    return JSON.stringify(value);
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.content === 'string') return obj.content;
    if (typeof obj.message === 'string') return obj.message;
    // content may itself be an array-of-text-blocks.
    if (Array.isArray(obj.content)) return extractSummaryText(obj.content);
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Pick the run's user-facing summary from the snapshot's run results: prefer the
 * LAST `run_agent` step's output (the terminal agent reply the user cares about),
 * falling back to the last result of any kind. Returns '' when there is no
 * usable output. Results are ordered by `step_order` to make "last" deterministic
 * regardless of array insertion order.
 */
function pickSummary(results: RunResult[]): string {
  if (results.length === 0) return '';
  const ordered = [...results].sort(
    (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0),
  );
  const lastAgent = [...ordered]
    .reverse()
    .find((r) => r.step_type === 'run_agent' && r.final_output != null);
  const chosen = lastAgent ?? ordered[ordered.length - 1];
  return extractSummaryText(chosen?.final_output);
}

/**
 * Pick the failing step's error message for a failed run: the last result that
 * carries an `error_message`, falling back to the last result. Used to surface
 * the failure reason inline instead of forcing the user into the editor.
 */
function pickError(results: RunResult[]): string {
  if (results.length === 0) return '';
  const ordered = [...results].sort(
    (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0),
  );
  const failing = [...ordered].reverse().find((r) => r.error_message);
  return failing?.error_message ?? ordered[ordered.length - 1]?.error_message ?? '';
}

const CompactRunProgressCard: React.FC<CompactRunProgressCardProps> = ({
  orchestrationId,
  runInstanceId,
  stepCount,
  projectId,
  workflowName,
}) => {
  // Drive our own live refresh until the run is terminal. `useExecution`
  // forwards options straight through to `useQuery` (per its JSDoc), so a
  // function-form `refetchInterval` reads the latest cached snapshot on each
  // tick: keep polling (2.5s) while non-terminal, return `false` to stop once
  // the run instance reaches a terminal status. This guarantees the card reaches
  // the terminal state on its own without depending on the editor's SSE handler.
  //
  // AC-1794 (#3c): a `paused` run (a user_prompt step waiting for the user's
  // answer) is NOT terminal, so polling continues — the card shows the input
  // box and, once the user submits and the run resumes (running -> completed),
  // the same poll picks up the new status and renders the summary.
  const { data } = useExecution(orchestrationId, {
    refetchInterval: (query) => {
      const s = query.state.data?.runInstance?.status as RunStatus | undefined;
      return s != null && TERMINAL_STATUSES.has(s) ? false : 2500;
    },
  });

  const queryClient = useQueryClient();

  // In-card answer for a paused user_prompt run. Controlled input; `submitting`
  // disables the form while the resume request is in flight.
  const [inputMessage, setInputMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The latest run snapshot for this workflow. When the confirm pre-created the
  // run instance but the run has not produced results yet, this is the pending
  // run; once `runWorkflow` starts producing results the SSE invalidation pulls
  // the live snapshot in. Default to 'pending' before the first snapshot lands.
  const status: RunStatus = (data?.runInstance?.status as RunStatus) ?? 'pending';

  const results = data?.runResults ?? [];
  const totalSteps = stepCount ?? results.length;
  // Completed steps = results in a terminal-success state. Used only to drive
  // the progress denominator visualization; the authoritative run status is the
  // run instance status above.
  const completedSteps = results.filter((r) => r.status === 'completed').length;
  const currentStep = Math.min(completedSteps, totalSteps);
  const progress =
    totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : 0;

  const isTerminal = TERMINAL_STATUSES.has(status);
  const showProgress = totalSteps > 0 && (status === 'running' || isTerminal);

  // On a COMPLETED run the bar reads 100% even if the result/denominator math
  // hasn't fully reconciled — a completed run is, by definition, fully done.
  const displayProgress = status === 'completed' ? 100 : progress;

  // Inline run RESULTS (no extra fetch — `results` is already in the snapshot).
  // On a completed run surface the terminal step's output in an expandable
  // disclosure; on a failed run surface the failing step's error message. Until
  // the run reaches a terminal state we show nothing here.
  const summaryText = status === 'completed' ? pickSummary(results) : '';
  const errorText = status === 'failed' ? pickError(results) : '';

  // Open-workflow deep link. Falls back to the project workflows list when the
  // owning project is unknown so the link is always reachable.
  const openHref =
    projectId != null
      ? `/projects/${projectId}/workflows/${orchestrationId}`
      : `/workflows`;

  // Status-driven heading. The previous version was ALWAYS `Running "<name>"`,
  // so the card read "Running" even after the run completed/failed — the
  // user-visible half of the freeze bug. The heading now reflects the actual
  // run status.
  // AC-1794 (#3c): a paused user_prompt run is waiting on the user. Surface
  // the in-card answer box and a heading that reads "Waiting for your input"
  // rather than "Running"/"Completed".
  const isPaused = status === 'paused';

  const quotedName = workflowName ? `"${workflowName}"` : 'workflow';
  const headingLabel = (() => {
    switch (status) {
      case 'completed':
        return `Ran ${quotedName} — completed`;
      case 'failed':
        return `${quotedName} — failed`;
      case 'stopped':
        return `${quotedName} — stopped`;
      case 'paused':
        return 'Waiting for your input';
      case 'pending':
        return `Starting ${quotedName}…`;
      case 'running':
      default:
        return `Running ${quotedName}…`;
    }
  })();

  // Submit the typed answer to resume the paused run. Forwards the answer to
  // `resumeWorkflowRun(runInstanceId, inputMessage)`; the backend injects it as
  // the prompt step's output and continues the run. After the resume request
  // returns we invalidate the execution query so the next poll observes the
  // now-RUNNING (then completed) status without waiting a full interval.
  const handleSubmitInput = async (e: React.FormEvent) => {
    e.preventDefault();
    // Don't resume on an empty answer (AC-1794, M4): the paused user_prompt
    // needs real input. Guards Enter-on-empty even though Submit is disabled.
    if (submitting || runInstanceId == null || !inputMessage.trim()) return;
    const answer = inputMessage;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiService.resumeWorkflowRun(String(runInstanceId), answer);
      setInputMessage('');
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowExecution(orchestrationId),
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to submit your answer.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="group"
      aria-label={headingLabel}
      aria-live="polite"
      data-run-instance-id={runInstanceId}
      className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-warm-white truncate" title={headingLabel}>
          {headingLabel}
        </span>
        <EnhancedStatusIndicator status={status} size="sm" />
      </div>

      {showProgress && (
        <EnhancedProgressBar
          progress={displayProgress}
          status={status}
          currentStep={status === 'completed' ? totalSteps : currentStep}
          totalSteps={totalSteps}
          showDetails={false}
        />
      )}

      {/* Inline run RESULTS. On a completed run, an expandable (keyboard-
          toggleable) <details> disclosure shows the terminal step's summary in a
          scrollable box; the Open-workflow link below remains for the full
          formatted view. No extra fetch — `summaryText` is derived from the
          snapshot already in cache. */}
      {summaryText && (
        <details className="text-xs">
          <summary className="cursor-pointer select-none font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-400 rounded">
            Summary
          </summary>
          <div className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-2 py-1.5 text-gray-800 dark:text-gray-200">
            {summaryText}
          </div>
        </details>
      )}

      {/* On a failed run, surface the failing step's error message inline
          (compact, red) instead of forcing the user into the editor to find it. */}
      {errorText && (
        <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-1.5 text-xs text-red-700 dark:text-red-300">
          {errorText}
        </div>
      )}

      {/* AC-1794 (#3c): a paused user_prompt run waits on the user. Render an
          in-card answer box + Submit BELOW the progress and ABOVE the
          Open-workflow link. Enter submits (single-line input inside a form);
          the answer is sent via `resumeWorkflowRun` and the existing poll
          surfaces the resulting completed summary. */}
      {isPaused && (
        <form onSubmit={handleSubmitInput} className="flex flex-col gap-1.5">
          <label
            htmlFor={`run-input-${runInstanceId}`}
            className="text-xs font-medium text-gray-700 dark:text-gray-300"
          >
            Your answer
          </label>
          <div className="flex items-stretch gap-2">
            <input
              id={`run-input-${runInstanceId}`}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={submitting}
              placeholder="Type your answer…"
              aria-label="Your answer"
              className="flex-1 min-w-0 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={submitting || !inputMessage.trim()}
              className="inline-flex items-center justify-center text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Sending…' : 'Submit'}
            </button>
          </div>
          {submitError && (
            <span role="alert" className="text-xs text-red-600 dark:text-red-400">
              {submitError}
            </span>
          )}
        </form>
      )}

      <Link
        to={openHref}
        className="inline-flex items-center justify-center gap-1 w-full min-h-[44px] text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
      >
        Open workflow
        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
      </Link>
    </div>
  );
};

export default CompactRunProgressCard;
